import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { ReconciliationState } from '../../../types/billing';
import { useApplyReconciliation } from '../../../services/billing';
import { errorMessageFrom } from '../../../api/client';
import { cn } from '@/lib/utils';

interface ReconciliationChooserProps {
  state: ReconciliationState;
}

const daysLeft = (iso: string | null): number => {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
};

/** One selectable row (member / course / program). */
const PickRow: React.FC<{
  label: string;
  detail?: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}> = ({ label, detail, checked, disabled, onToggle }) => (
  <label
    className={cn(
      'flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors duration-150',
      checked
        ? 'border-primary-500/50 bg-primary-500/5 dark:bg-primary-350/10'
        : 'border-border-primary hover:border-primary-300',
      disabled && 'cursor-not-allowed opacity-70'
    )}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onToggle}
      className="h-4 w-4 accent-primary-500"
    />
    <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
      {label}
    </span>
    {detail && <span className="shrink-0 text-xs text-text-tertiary">{detail}</span>}
  </label>
);

/**
 * The choose-what-stays screen (decision 2026-08-03): after a downgrade,
 * the admin picks which members stay active and which content stays
 * published within the new plan's caps. Everything else is parked —
 * deactivated or set to draft — never deleted, and members return
 * automatically on upgrade.
 */
const ReconciliationChooser: React.FC<ReconciliationChooserProps> = ({ state }) => {
  const apply = useApplyReconciliation();
  const inv = state.inventory;

  const memberCap = state.caps.members;
  const courseCap = state.caps.courses;
  const programCap = state.caps.programs;

  const membersOver = Boolean(state.overages.members);
  const coursesOver = Boolean(state.overages.courses);
  const programsOver = Boolean(state.overages.programs);

  const selfId = useMemo(
    () => inv?.members.find((m) => m.is_self)?.id ?? null,
    [inv]
  );

  // Defaults: keep yourself; everything within cap stays checked implicitly.
  const [members, setMembers] = useState<Set<string>>(
    () => new Set(selfId ? [selfId] : [])
  );
  const [courses, setCourses] = useState<Set<string>>(() => new Set());
  // Folded by default — a 100-member workspace would otherwise open a wall
  // of checkboxes before the admin even asked for it (Aleix, 2026-08-10).
  const [expanded, setExpanded] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');

  if (!state.open || !inv) return null;

  const visibleMembers = memberQuery.trim()
    ? inv.members.filter((m) =>
        m.name.toLowerCase().includes(memberQuery.trim().toLowerCase())
      )
    : inv.members;

  const toggle = (
    set: Set<string>,
    setter: (s: Set<string>) => void,
    id: string,
    cap: number | null
  ) => {
    const next = new Set(set);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (cap !== null && next.size >= cap) {
        toast(`You can keep at most ${cap} on the ${state.plan} plan.`, { icon: 'ℹ️' });
        return;
      }
      next.add(id);
    }
    setter(next);
  };

  const handleSubmit = () => {
    const parkedMembers = membersOver ? inv.members.length - members.size : 0;
    const parkedCourses = coursesOver ? inv.courses.length - courses.size : 0;
    const parkedPrograms = programsOver ? inv.programs.length : 0;
    const summary = [
      parkedMembers > 0 && `${parkedMembers} member(s) will be deactivated`,
      parkedCourses > 0 && `${parkedCourses} course(s) will move to drafts`,
      parkedPrograms > 0 && `${parkedPrograms} program(s) will be paused`,
    ]
      .filter(Boolean)
      .join(', ');
    if (
      !window.confirm(
        `Apply these choices? ${summary}. Nothing is deleted — members return automatically if you upgrade, and content can be re-published within your limits.`
      )
    ) {
      return;
    }
    apply.mutate(
      {
        keep_membership_ids: membersOver ? [...members] : inv.members.map((m) => m.id),
        keep_course_ids: coursesOver ? [...courses] : inv.courses.map((c) => c.id),
        keep_program_ids: [],
      },
      {
        onSuccess: () => toast.success('Done — your workspace now fits its plan.'),
        onError: (err) =>
          toast.error(errorMessageFrom(err) ?? 'Could not apply your choices.'),
      }
    );
  };

  const days = daysLeft(state.deadline);

  return (
    <div
      data-testid="reconciliation-chooser"
      className="mb-8 rounded-2xl border border-secondary-500/40 bg-surface-elevated p-6"
    >
      <h3 className="text-base font-semibold text-text-primary">
        Choose what stays on the {state.plan.charAt(0).toUpperCase() + state.plan.slice(1)} plan
      </h3>
      <p className="mt-1 max-w-[72ch] text-sm text-text-secondary">
        Your plan changed and the workspace holds more than the new limits allow.
        Pick what stays — everything else is parked, never deleted: members return
        automatically if you upgrade, and drafts can be re-published within your
        limits.{' '}
        {state.frozen ? (
          <span className="font-semibold text-error-600 dark:text-error-400">
            The grace period has ended — managing the workspace is locked until you choose.
          </span>
        ) : (
          <span className="font-semibold">
            {days} day{days === 1 ? '' : 's'} left before management locks.
          </span>
        )}
      </p>

      {!expanded && (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold transition-[background-color,transform] duration-150 hover:bg-primary-600 active:scale-[0.97] dark:hover:bg-primary-400"
          >
            Choose what stays
            {membersOver && ` · ${inv.members.length} members → ${memberCap}`}
            {coursesOver && ` · ${inv.courses.length} courses → ${courseCap}`}
          </button>
          <button
            type="button"
            onClick={() =>
              document
                .getElementById('tier-plans')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            className="inline-flex items-center gap-2 rounded-full border border-secondary-500/50 px-5 py-2.5 text-sm font-bold text-secondary-600 transition-[background-color,transform] duration-150 hover:bg-secondary-500/[0.08] active:scale-[0.97] dark:text-secondary-400"
          >
            Or upgrade and keep everyone ↓
          </button>
        </div>
      )}

      {expanded && membersOver && (
        <div className="mt-5">
          <div className="mb-2 flex items-baseline justify-between">
            <h4 className="text-sm font-bold text-text-primary">Members</h4>
            <span
              className={cn(
                'text-xs font-semibold',
                members.size === memberCap ? 'text-success-600' : 'text-text-tertiary'
              )}
            >
              keeping {members.size} / {memberCap}
            </span>
          </div>
          {inv.members.length > 12 && (
            <input
              type="search"
              value={memberQuery}
              onChange={(e) => setMemberQuery(e.target.value)}
              placeholder={`Search ${inv.members.length} members…`}
              className="mb-3 w-full rounded-xl border border-border-primary bg-surface-primary px-3.5 py-2 text-sm text-text-primary outline-none transition-colors focus:border-primary-500"
            />
          )}
          <div className="grid max-h-[420px] gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
            {visibleMembers.map((m) => (
              <PickRow
                key={m.id}
                label={m.is_self ? `${m.name} (you)` : m.name}
                detail={m.role}
                checked={members.has(m.id)}
                disabled={m.is_self}
                onToggle={() => toggle(members, setMembers, m.id, memberCap)}
              />
            ))}
            {visibleMembers.length === 0 && (
              <p className="text-sm text-text-tertiary">No members match that search.</p>
            )}
          </div>
        </div>
      )}

      {expanded && coursesOver && (
        <div className="mt-5">
          <div className="mb-2 flex items-baseline justify-between">
            <h4 className="text-sm font-bold text-text-primary">Published courses</h4>
            <span
              className={cn(
                'text-xs font-semibold',
                courses.size === courseCap ? 'text-success-600' : 'text-text-tertiary'
              )}
            >
              keeping {courses.size} / {courseCap}
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {inv.courses.map((c) => (
              <PickRow
                key={c.id}
                label={c.title}
                checked={courses.has(c.id)}
                onToggle={() => toggle(courses, setCourses, c.id, courseCap)}
              />
            ))}
          </div>
        </div>
      )}

      {expanded && programsOver && (
        <div className="mt-5">
          <h4 className="text-sm font-bold text-text-primary">Learning programs</h4>
          <p className="mt-1 text-sm text-text-secondary">
            {programCap === 0
              ? `Programs aren't available on this plan — your ${inv.programs.length} active program(s) will be paused (not deleted).`
              : `Your plan allows ${programCap} active program(s).`}
          </p>
        </div>
      )}

      {expanded && (
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={apply.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold transition-[background-color,transform] duration-150 hover:bg-primary-600 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-primary-400"
        >
          {apply.isPending ? 'Applying…' : 'Apply my choices'}
        </button>
        {/* The friendlier way out — and the one that pays the bills. */}
        <button
          type="button"
          onClick={() =>
            document
              .getElementById('tier-plans')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          className="inline-flex items-center gap-2 rounded-full border border-secondary-500/50 px-5 py-2.5 text-sm font-bold text-secondary-600 transition-[background-color,transform] duration-150 hover:bg-secondary-500/[0.08] active:scale-[0.97] dark:text-secondary-400"
        >
          Or upgrade and keep everyone ↓
        </button>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-sm font-semibold text-text-tertiary underline underline-offset-2 transition-colors hover:text-text-secondary"
        >
          Fold the list
        </button>
      </div>
      )}
    </div>
  );
};

export default ReconciliationChooser;
